import type { SessionUser } from './claims';

/**
 * Authentication backend abstraction. Two implementations exist:
 *  - {@link createCognitoProvider} — real Amazon Cognito User Pool auth.
 *  - {@link createDevProvider}     — a UX-only mock for local dev with no pool.
 *
 * The active one is chosen by config at startup (see {@link resolveAuthMode}),
 * so the rest of the app (AuthContext, guards, screens) is identical in both.
 * Neither is a security boundary — the AWS backend verifies every JWT.
 */
export type AuthMode = 'cognito' | 'dev';

/** Result of a sign-in attempt. */
export type SignInResult =
  | { status: 'ok'; session: AuthSession }
  | { status: 'new_password_required' }; // first-login flow; surfaced to the user.

/** A live session: the user profile plus the current ID token for the API client. */
export interface AuthSession {
  user: SessionUser;
  /** Cognito **ID token** (JWT). Injected as the API bearer token. */
  idToken: string;
  /** Epoch seconds at which `idToken` expires (for proactive refresh). */
  expiresAt: number;
}

/** Credentials accepted by every provider. The dev provider also reads an optional role. */
export interface SignInInput {
  email: string;
  password: string;
}

export interface AuthProvider {
  readonly mode: AuthMode;
  /** Restore a persisted session on app load (silent refresh if needed). null when signed out. */
  restore: () => Promise<AuthSession | null>;
  /** Sign in with email + password. */
  signIn: (input: SignInInput) => Promise<SignInResult>;
  /** Return a valid ID token, refreshing if it is expired/near expiry. null when signed out. */
  getValidIdToken: () => Promise<string | null>;
  /** Clear the persisted session everywhere. */
  signOut: () => Promise<void>;
}

/**
 * Pure provider-selection logic (unit-tested). Real Cognito auth is used when BOTH
 * public ids are present. The dev mock is ONLY ever selected in a non-production build
 * (`pnpm dev:web`) so a misconfigured PRODUCTION build can never silently grant a
 * dev "log in as Admin". In prod we always return 'cognito' — if its config is missing
 * the provider fails loudly at sign-in rather than admitting anyone. (The AWS backend
 * also rejects the unsigned dev token, so this is defence-in-depth.) Whitespace-only
 * values count as unset.
 */
export function resolveAuthMode(
  config: { userPoolId?: string | null; userPoolClientId?: string | null },
  isProduction = false,
): AuthMode {
  const hasPool = Boolean(config.userPoolId && config.userPoolId.trim());
  const hasClient = Boolean(config.userPoolClientId && config.userPoolClientId.trim());
  if (hasPool && hasClient) return 'cognito';
  return isProduction ? 'cognito' : 'dev';
}

/** Seconds before expiry at which a token is considered "due for refresh". */
export const TOKEN_REFRESH_SKEW_SECONDS = 60;

/** True when a token expiring at `expiresAt` (epoch seconds) should be refreshed now. */
export function isTokenExpiring(expiresAt: number, nowMs: number = Date.now()): boolean {
  return expiresAt - TOKEN_REFRESH_SKEW_SECONDS <= Math.floor(nowMs / 1000);
}
